import type { IntlShape } from 'react-intl'
import { appendContextTag, TRIP_PLANNER_ACTION_CONTEXT_LABEL } from 'tripPlanner/utils/contextTag'
import { getLocalisedDayLabel } from 'tripPlanner/utils/chatDates'
import { isFlightCardSavedItemData, type TransportOption } from 'tripPlanner/api/types'

// Builders for the deterministic "Trip planner action" messages the FE sends
// when the user deletes an item, stop, or day from the chat UI. Each builder
// returns the full visible sentence + a hidden <Context> block carrying the
// entity ID. The agent reads [CURRENT TRIP STATE] to pick the right DSL
// command (DELETE_ITEM / DELETE_OFFER / DELETE_FLIGHT / DELETE_TRANSPORT /
// DELETE_STOP / DELETE_DAY) — the FE is intentionally type-agnostic.

interface Stop {
  id: number
  placeName: string
}

interface Day {
  id: number
  dayNumber: number
  date?: string
}

interface Item {
  id: number
  name: string
}

export interface DeleteItemMessageArgs {
  intl: IntlShape
  item: Item
  /** Omit for inter-stop items (flights / transport). */
  stop?: Stop
  /** Omit for multi-day stays, highlights, to-be-planned items, and inter-stop items. */
  day?: Day
}

export interface DeleteStopMessageArgs {
  intl: IntlShape
  stop: Stop
}

export interface DeleteDayMessageArgs {
  intl: IntlShape
  stop: Stop
  day: Day
}

export function buildDeleteItemMessage({ intl, item, stop, day }: DeleteItemMessageArgs): string {
  let visible: string
  if (stop && day) {
    const dayLabel = getLocalisedDayLabel(intl, day.dayNumber, day.date)
    visible = intl.formatMessage(
      {
        id: 'RusIOwWLSzTxlcTtbHgq',
        defaultMessage: 'Delete "{name}" from {dayLabel} in {stopName}',
        description: 'User chat message shown when deleting an item assigned to a specific day in a stop',
      },
      { name: item.name, dayLabel, stopName: stop.placeName },
    )
  } else if (stop) {
    visible = intl.formatMessage(
      {
        id: 'p3YGyt72nvnd1y58O_Rc',
        defaultMessage: 'Delete "{name}" from {stopName}',
        description: 'User chat message shown when deleting an item from a stop without a day assignment (e.g. highlight, to-be-planned, or ongoing stay)',
      },
      { name: item.name, stopName: stop.placeName },
    )
  } else {
    visible = intl.formatMessage(
      {
        id: 'PQKi_A0TsOcs_qlFQ-6l',
        defaultMessage: 'Delete "{name}"',
        description: 'User chat message shown when deleting an inter-stop item such as a flight or other transport',
      },
      { name: item.name },
    )
  }
  const hidden = `Delete item_id=${item.id} ("${item.name}")`
  return appendContextTag(visible, hidden, TRIP_PLANNER_ACTION_CONTEXT_LABEL)
}

export function buildDeleteStopMessage({ intl, stop }: DeleteStopMessageArgs): string {
  const visible = intl.formatMessage(
    {
      id: '-DI8sSDHBmTyCzcDoHEw',
      defaultMessage: 'Delete "{placeName}" from my trip',
      description: 'User chat message shown when deleting an entire stop from the trip itinerary',
    },
    { placeName: stop.placeName },
  )
  const hidden = `Delete stop_id=${stop.id} ("${stop.placeName}")`
  return appendContextTag(visible, hidden, TRIP_PLANNER_ACTION_CONTEXT_LABEL)
}

// Flights are inter-stop items — build a human-friendly display name from
// savedItemData when we have it, otherwise fall back to the option label or
// connection name. Used by the delete-message builder and by the deletion
// snackbar name-lookup helpers.
export function buildFlightName(option: TransportOption, fallback: string): string {
  const saved = option.savedItemData
  if (!isFlightCardSavedItemData(saved)) return option.label || fallback
  const route = saved.departureAirport && saved.arrivalAirport
    ? `${saved.departureAirport} → ${saved.arrivalAirport}`
    : undefined
  return [saved.airline, saved.flightNumber, route].filter(Boolean).join(' ')
    || option.label
    || fallback
}

export function buildDeleteDayMessage({ intl, stop, day }: DeleteDayMessageArgs): string {
  const dayLabel = getLocalisedDayLabel(intl, day.dayNumber, day.date)
  const visible = intl.formatMessage(
    {
      id: 'vUoo1zEfnlZr_J4qFaFE',
      defaultMessage: 'Delete {dayLabel} in {stopName}',
      description: 'User chat message shown when deleting a day from a stop in the trip itinerary',
    },
    { dayLabel, stopName: stop.placeName },
  )
  const hidden = `Delete day_id=${day.id}`
  return appendContextTag(visible, hidden, TRIP_PLANNER_ACTION_CONTEXT_LABEL)
}
